import Decimal from 'decimal.js';
import type { NextApiRequest, NextApiResponse } from 'next';
import type { Maybe } from 'yup';

import type { SalesforceLanguageCodeEnum } from '@/types/regionTypes';
import { removeLocaleFromUrl } from '@/utils/locale';

import type { CountryCode } from '@/graphql/saleor/api';
import { paths } from '@/lib/paths';
import ecsConfigurations from '@/lib/service/ecs.config.json';

import { ECS_ACCESS_PASSWORD, ECS_ACCESS_USERNAME } from '../const';
import { getLocaleForCountryAndLanguage } from '../regions';
import { isValidCountryCode } from '../regions';
import type {
  EcsConfig,
  QuoteDetail,
  QuoteRepairItem,
  RepairService,
} from './type';

export const formatServiceUniqueId = (
  repairItems: Maybe<QuoteRepairItem[]>,
): QuoteRepairItem[] => {
  if (!repairItems) return [];
  return repairItems.map((repairItem) => ({
    ...repairItem,
    services: repairItem.services.map((service) => ({
      ...service,
      id: repairItem.itemId + service.id,
    })),
  }));
};

export const filterAcceptedServices = (
  quoteDetail: QuoteDetail,
  acceptedServieCodes: string[],
) => {
  const acceptedRepairItems: QuoteRepairItem[] = [];
  const acceptedVariousServices: RepairService[] = [];
  A: for (const code of acceptedServieCodes) {
    if (quoteDetail.repairItems) {
      for (const repairItem of quoteDetail.repairItems) {
        for (const service of repairItem.services) {
          if (repairItem.itemId + service.id === code) {
            const acceptedRepairItem = acceptedRepairItems.find(
              (item) => item.itemId === repairItem.itemId,
            );
            if (acceptedRepairItem) {
              acceptedRepairItem.services.push(service);
            } else {
              acceptedRepairItems.push({
                ...repairItem,
                services: [service],
              });
            }
            continue A;
          }
        }
      }
    }
    if (quoteDetail.variousServices) {
      for (const variousService of quoteDetail.variousServices) {
        if (variousService.id === code) {
          acceptedVariousServices.push(variousService);
          break;
        }
      }
    }
  }
  return { acceptedRepairItems, acceptedVariousServices };
};

const servicePriceStatistics = (services: RepairService[]) => {
  let optionalServicePrice = 0;
  let mandatoryServicePrice = 0;
  let salesTax = 0;
  services.forEach((service) => {
    if (service.optional) {
      optionalServicePrice = new Decimal(optionalServicePrice)
        .plus(new Decimal(service.finalPrice ?? 0))
        .toNumber();
    } else {
      mandatoryServicePrice = new Decimal(mandatoryServicePrice)
        .plus(new Decimal(service.finalPrice ?? 0))
        .toNumber();
    }
    salesTax = new Decimal(salesTax)
      .plus(new Decimal(service.taxes ?? 0))
      .toNumber();
  });
  return {
    optionalServicePrice,
    mandatoryServicePrice,
    salesTax,
  };
};

export const handleServicePrices = (
  repairItems: QuoteRepairItem[],
  variousServices: RepairService[],
) => {
  let optionalServiceTotalPrice = 0;
  let mandatoryServiceTotalPrice = 0;
  let totalSalesTax = 0;
  repairItems.forEach((repairItem) => {
    const { mandatoryServicePrice, optionalServicePrice, salesTax } =
      servicePriceStatistics(repairItem.services);
    optionalServiceTotalPrice = new Decimal(optionalServiceTotalPrice)
      .plus(new Decimal(optionalServicePrice))
      .toNumber();
    mandatoryServiceTotalPrice = new Decimal(mandatoryServiceTotalPrice)
      .plus(new Decimal(mandatoryServicePrice))
      .toNumber();
    totalSalesTax = new Decimal(totalSalesTax)
      .plus(new Decimal(salesTax))
      .toNumber();
  });
  const { mandatoryServicePrice, optionalServicePrice, salesTax } =
    servicePriceStatistics(variousServices);
  optionalServiceTotalPrice = new Decimal(optionalServiceTotalPrice)
    .plus(new Decimal(optionalServicePrice))
    .toNumber();
  mandatoryServiceTotalPrice = new Decimal(mandatoryServiceTotalPrice)
    .plus(new Decimal(mandatoryServicePrice))
    .toNumber();
  totalSalesTax = new Decimal(totalSalesTax)
    .plus(new Decimal(salesTax))
    .toNumber();
  return {
    optionalServicePrice: optionalServiceTotalPrice,
    mandatoryServicePrice: mandatoryServiceTotalPrice,
    salesTax: totalSalesTax,
  };
};

export const getContactPathWithLocale = (locale?: string) => {
  return `/${locale ?? 'us-en'}` + paths.contact.asPath();
};

export const getRedirectUrl = (
  countryCode: CountryCode,
  localeCountryCode: CountryCode,
  localeLanguageCode: SalesforceLanguageCodeEnum,
  path: string,
) => {
  if (localeCountryCode !== countryCode) {
    const locale = getLocaleForCountryAndLanguage(
      countryCode,
      localeLanguageCode,
      false,
    );
    const resolvedUrlWithoutLocale = removeLocaleFromUrl(path);
    return `/${locale}${resolvedUrlWithoutLocale}`;
  }
  return null;
};

export const validateRequestBySSR = (
  req: NextApiRequest,
  res: NextApiResponse,
  method: NextApiRequest['method'] = 'GET',
) => {
  if (req.method !== method) {
    res.status(405).json({
      code: 405,
      msg: 'Method Not Allowed',
      data: null,
    });
    return false;
  }
  if (!ECS_ACCESS_USERNAME || !ECS_ACCESS_PASSWORD) {
    res.status(401).json({
      code: 401,
      msg: 'Authorization header is missing',
      data: null,
    });
    return false;
  }
  const { countryCode } = req.query;
  if (!countryCode || typeof countryCode !== 'string') {
    res.status(400).json({
      code: 400,
      msg: 'Invalid parameters: countryCode is required and must be string',
      data: null,
    });
    return false;
  }

  if (!isValidCountryCode(countryCode)) {
    res.status(200).json({
      code: 400,
      msg: 'Invalid country code',
      data: null,
    });
    return false;
  }

  return true;
};

export const getEcsConfigurations = (): EcsConfig[] => {
  return ecsConfigurations as EcsConfig[];
};

export const getEcsConfigurationByCountry = (
  country: CountryCode,
): Maybe<EcsConfig> => {
  return getEcsConfigurations()?.find((item) => item.countryCode === country);
};
